/**
 * DomainPanelGrid — 2-column accordion grid of 7 domain panels.
 *
 * Behavior:
 *   - Single-open: clicking a panel opens it and closes any other.
 *   - Toggle All: expands/collapses all panels at once.
 *   - scrollIntoView: opened panel scrolls to center of viewport.
 *   - Each panel header shows: accent bar + domain name + status chips + 3 KPI minis + chevron.
 *   - Expanded body shows: 3 metric cells + signal items + "View [Domain] →" link.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronsUpDown, Info } from 'lucide-react';
import { cn } from '../ui/lib/utils';
import { SectionHeading } from '../ui/SectionHeading';
import { StatusBadge } from '../ui/StatusBadge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { ICON_PX } from '../ui/lib/iconSizes';
import { fetchDomainSummary } from '../../utils/api';
import { useConsumerUnit } from '../../contexts/ConsumerUnitContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEventStream } from '../../hooks/useEventStream';

// ─── Domain structural skeleton (empty until API data arrives) ────────────────
const EMPTY_DOMAINS = [
  { key: 'staffing',  name: 'Staffing',            accent: 'var(--yellow)',      route: '/manager/staffing',           chips: [], kpis: [], metrics: [], signals: [] },
  { key: 'access',    name: 'Scheduling & Access',  accent: 'var(--aqua)',        route: '/manager/access',             chips: [], kpis: [], metrics: [], signals: [] },
  { key: 'health',    name: 'Population Health',    accent: 'var(--teal-dark)',   route: '/manager/population-health',  chips: [], kpis: [], metrics: [], signals: [] },
  { key: 'finance',   name: 'Financial Tracking',   accent: 'var(--green)',       route: '/manager/finance',            chips: [], kpis: [], metrics: [], signals: [] },
  { key: 'consumer',  name: 'Consumer Insights',    accent: 'var(--orange-warn)', route: '/manager/consumer-insights',  chips: [], kpis: [], metrics: [], signals: [] },
  { key: 'practice',  name: 'Practice Management',  accent: 'var(--royal)',       route: '/manager/practice-management',chips: [], kpis: [], metrics: [], signals: [] },
];

const SIGNAL_DOT = {
  critical: 'bg-red-critical',
  watch: 'bg-yellow-nn',
  info: 'bg-aqua',
};

const METRIC_COLOR = {
  critical: 'text-red-critical',
  watch: 'text-yellow-nn',
  ok: 'text-green-nn',
};

// ─── Panel component ─────────────────────────────────────────────────────────
function DomainPanel({ domain, isOpen, onToggle }) {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const d = domain;

  return (
    <div
      ref={panelRef}
      className={cn(
        'rounded-card bg-nn-white shadow-card border border-nn-border overflow-hidden',
        'transition-shadow hover:shadow-md',
        'animate-fade-up'
      )}
    >
      {/* Header — always visible */}
      <button
        type="button"
        onClick={() => {
          onToggle();
          if (!isOpen) {
            setTimeout(() => {
              panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 350);
          }
        }}
        className={cn(
          'w-full flex items-center cursor-pointer text-left',
          isOpen && 'border-b border-nn-border'
        )}
      >
        {/* Accent bar */}
        <div className="w-[3px] self-stretch shrink-0" style={{ background: d.accent }} />

        {/* Inner content */}
        <div className="flex items-center gap-2 flex-1 min-w-0 px-3 py-2">
          {/* Domain name */}
          <span className="text-[13px] font-semibold text-text-dark whitespace-nowrap shrink-0">
            {d.name}
          </span>

          {/* Status chips */}
          <div className="flex items-center gap-1 shrink-0">
            {d.chips.map((c) => (
              <StatusBadge key={c.text} variant={c.variant} size="sm">{c.text}</StatusBadge>
            ))}
          </div>

          {/* KPI mini strip — pushed right */}
          <div className="flex items-center gap-3 ml-auto shrink-0 pr-1">
            {d.kpis.map((k) => (
              <div key={k.label} className="text-right">
                <div className="text-[9px] text-text-muted leading-tight">{k.label}</div>
                <div className={cn(
                  'text-[12px] font-bold leading-tight',
                  k.alert ? 'text-red-critical' : 'text-text-dark'
                )}>
                  {k.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chevron */}
        <div className={cn(
          'shrink-0 px-3 py-2 text-text-muted transition-transform',
          isOpen && 'rotate-180'
        )}>
          <ChevronDown size={14} />
        </div>
      </button>

      {/* Body — expanded content */}
      <div
        className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{ maxHeight: isOpen ? 360 : 0 }}
      >
        <div className="p-3">
          {/* Metric cells */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {d.metrics.map((m) => (
              <div key={m.label} className="bg-surface rounded p-2">
                <div className="text-[10px] text-text-muted leading-tight">{m.label}</div>
                <div className={cn(
                  'text-[16px] font-bold leading-tight mt-0.5',
                  METRIC_COLOR[m.status] || 'text-text-dark'
                )}>
                  {m.value}
                </div>
                {m.sub && <div className="text-[9px] text-text-muted mt-0.5">{m.sub}</div>}
              </div>
            ))}
          </div>

          {/* Signal items */}
          <div className="flex flex-col gap-1.5 mb-3">
            {d.signals.map((s, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className={cn('w-1.5 h-1.5 rounded-full mt-1.5 shrink-0', SIGNAL_DOT[s.severity])} />
                <span className="text-[11px] text-text-dark leading-snug flex-1">{s.text}</span>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            {d.routeDisabled ? (
              <span className="text-[11px] font-semibold text-text-muted">
                View {d.name} → (coming soon)
              </span>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(d.route); }}
                className="text-[11px] font-semibold text-aqua hover:underline"
              >
                View {d.name} →
              </button>
            )}
            <span className="text-[9px] text-text-muted">Updated 7:01 AM</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main grid ───────────────────────────────────────────────────────────────
// Row pairs — panels that share a grid row open together
const ROW_PAIRS = {
  staffing: 'access',    access: 'staffing',
  health: 'finance',     finance: 'health',
  consumer: 'practice',  practice: 'consumer',
};

export default function DomainPanelGrid() {
  const [domains, setDomains] = useState(EMPTY_DOMAINS);
  const [openKeys, setOpenKeys] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const sectionRef = useRef(null);
  const { selectedUnitId } = useConsumerUnit();
  const { getToken } = useAuth();

  // Fetch domain summary from API on mount and when unit changes; fall back to EMPTY_DOMAINS
  useEffect(() => {
    let cancelled = false;
    fetchDomainSummary(selectedUnitId, getToken).then((data) => {
      if (cancelled) return;
      if (data && Array.isArray(data.domains) && data.domains.length > 0) {
        setDomains(data.domains);
      }
    }).catch(() => { /* keep EMPTY_DOMAINS fallback */ });
    return () => { cancelled = true; };
  }, [selectedUnitId, getToken]);

  // SSE: live domain updates — merge incoming domain data into domains state
  const { subscribe } = useEventStream();
  useEffect(() => {
    const unsub = subscribe('domain.updated', (data) => {
      if (!data.domain) return;
      setDomains((prev) => prev.map((d) => {
        if (d.key !== data.domain) return d;
        return {
          ...d,
          ...(data.kpis ? { kpis: data.kpis } : {}),
          ...(data.metrics ? { metrics: data.metrics } : {}),
          ...(data.signals ? { signals: data.signals } : {}),
          ...(data.chips ? { chips: data.chips } : {}),
        };
      }));
    });
    return unsub;
  }, [subscribe]);

  const togglePanel = useCallback((key) => {
    if (expandAll) {
      setExpandAll(false);
      const pair = ROW_PAIRS[key];
      setOpenKeys(new Set(pair ? [key, pair] : [key]));
      return;
    }
    setOpenKeys((prev) => {
      if (prev.has(key)) {
        // Closing — clear the row pair
        return new Set();
      }
      // Opening — open this panel + its row-mate
      const pair = ROW_PAIRS[key];
      return new Set(pair ? [key, pair] : [key]);
    });
  }, [expandAll]);

  const toggleAll = () => {
    if (expandAll) {
      // Collapse all — scroll section heading back into view
      setExpandAll(false);
      setOpenKeys(new Set());
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 350);
    } else {
      // Expand all — scroll section heading to top so panels are visible
      setExpandAll(true);
      setOpenKeys(new Set());
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 350);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div ref={sectionRef} className="mt-2">
        <SectionHeading
          title="Domain Dashboards"
          size="sm"
          className="mb-1.5"
          aiContext="practice-pulse.domain-health"
          action={
            <div className="flex items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"
                  >
                    <Info size={ICON_PX.md} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" maxWidth={220}>
                  At-a-glance status for each operational domain. Click a panel to expand its details, or Toggle All to see everything.
                </TooltipContent>
              </Tooltip>
              <button
                type="button"
                onClick={toggleAll}
                className={cn(
                  'flex items-center gap-1 text-[11px] font-semibold',
                  'text-text-dark-secondary bg-surface-warm border border-nn-border',
                  'rounded px-2 py-1 hover:bg-surface transition-colors'
                )}
              >
                <ChevronsUpDown size={12} />
                {expandAll ? 'Collapse All' : 'Expand All'}
              </button>
            </div>
          }
        />

        <div className="grid grid-cols-2 gap-2.5">
          {domains.map((d) => (
            <DomainPanel
              key={d.key}
              domain={d}
              isOpen={expandAll || openKeys.has(d.key)}
              onToggle={() => togglePanel(d.key)}
            />
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
